import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Card,
  Chip,
  Container,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Avatar, HeaderBreadcrumbs, Page, Scrollbar } from '@/components';
import { SkeletonLoadingTable } from '@/components/skeleton';
import { TableHeadCustom, TableNoData } from '@/components/table';
import useSettings from '@/hooks/useSettings';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import { PATH_USER } from '@/features/app';
import { clientManagementPath } from '@/features/users/clients';
import { getUsersByType } from '@/features/users/shared/api';
import { getInvoiceDryRunProviderInvoice } from '@/features/invoices/api';
import type { InvoiceDryRunProviderInvoiceLineItem } from '@/features/invoices/types';
import { invoicesPath } from '../routes/paths';
import {
  formatDryRunDateRange,
  formatDryRunDateTime,
  formatDryRunMoney,
  getDryRunInvoiceLineItems,
  INVOICE_DRY_RUN_LINE_ITEMS_TABLE_HEAD,
} from '../utils';

type SummaryItemProps = {
  label: string;
  value: React.ReactNode;
  helper?: React.ReactNode;
};

type LineItemTotals = {
  hours: number;
  subtotal: number;
  cleaningProducts: number;
  total: number;
};

function SummaryItem({ label, value, helper }: SummaryItemProps) {
  return (
    <Box
      sx={{
        minHeight: 76,
        p: 1.75,
        borderRadius: 1,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper',
      }}
    >
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography variant="subtitle2" sx={{ mt: 0.5, lineHeight: 1.4 }}>
        {value}
      </Typography>
      {helper && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {helper}
        </Typography>
      )}
    </Box>
  );
}

const toNumber = (value?: number | string | null) => {
  if (value === null || value === undefined || value === '') return 0;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const getStringRecordValue = (
  source: Record<string, unknown>,
  keys: string[]
) => {
  const value = keys
    .map((key) => source[key])
    .find((item) => typeof item === 'string' && item.trim());

  return typeof value === 'string' ? value.trim() : '';
};

const formatClientName = (item: InvoiceDryRunProviderInvoiceLineItem) => {
  const clientRecord = (item.client ?? {}) as Record<string, unknown>;
  const firstName = getStringRecordValue(clientRecord, [
    'first_name',
    'firstName',
  ]);
  const lastName = getStringRecordValue(clientRecord, [
    'last_name',
    'lastName',
  ]);
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  const namedValue =
    fullName ||
    getStringRecordValue(clientRecord, [
      'name',
      'display_name',
      'displayName',
      'full_name',
      'fullName',
      'email',
    ]);

  if (namedValue) return namedValue;
  if (item.client_id) return `Client ${item.client_id}`;

  return 'Client';
};

const hasClientDisplayName = (
  client: InvoiceDryRunProviderInvoiceLineItem['client']
) => {
  const clientRecord = (client ?? {}) as Record<string, unknown>;
  return Boolean(
    getStringRecordValue(clientRecord, [
      'first_name',
      'firstName',
      'last_name',
      'lastName',
      'name',
      'display_name',
      'displayName',
      'full_name',
      'fullName',
      'email',
    ])
  );
};

const resolveLineItemClient = (
  item: InvoiceDryRunProviderInvoiceLineItem,
  clientsById:
    | Record<string, InvoiceDryRunProviderInvoiceLineItem['client']>
    | undefined
) => {
  const clientFromList = item.client_id
    ? clientsById?.[String(item.client_id)]
    : undefined;

  if (hasClientDisplayName(item.client)) return item.client;
  if (clientFromList) return clientFromList;

  return item.client ?? null;
};

function ClientAccountLink({
  item,
}: {
  item: InvoiceDryRunProviderInvoiceLineItem;
}) {
  const clientId = item.client?.id ?? item.client_id;

  if (!clientId) return <Typography variant="body2">-</Typography>;

  const clientName = formatClientName(item);
  const profileImage = item.client?.profile_image;

  return (
    <Box
      component={RouterLink}
      to={clientManagementPath('edit', {
        userId: String(clientId),
        tabSid: 'details',
      })}
      sx={{ display: 'inline-flex', textDecoration: 'none' }}
    >
      <Chip
        size="small"
        clickable
        avatar={
          <Avatar alt={clientName} src={profileImage}>
            {clientName.charAt(0).toUpperCase()}
          </Avatar>
        }
        label={clientName}
        title={`Open client ${clientId}`}
      />
    </Box>
  );
}

export default function InvoiceDryRunProviderInvoiceDetails() {
  const theme = useTheme();
  const { themeStretch } = useSettings();
  const { errorMsg } = useSnackbarMsg();
  const { id = '', invoiceId = '' } = useParams();

  const {
    data: invoice,
    isError,
    isLoading,
  } = useQuery({
    queryKey: ['invoiceDryRunProviderInvoice', id, invoiceId],
    queryFn: () => getInvoiceDryRunProviderInvoice(id, invoiceId),
    enabled: !!id && !!invoiceId,
    onError: (error) => {
      errorMsg(
        error,
        'Something went wrong while fetching provider invoice preview'
      );
    },
  });
  const { data: clients } = useQuery({
    queryKey: ['clients', 'list', 'all'],
    queryFn: () => getUsersByType(1),
    onError: (error) => {
      errorMsg(error, 'Something went wrong while fetching clients');
    },
  });

  const lineItems = getDryRunInvoiceLineItems(invoice);
  const clientsById = useMemo(
    () =>
      clients?.reduce<
        Record<string, InvoiceDryRunProviderInvoiceLineItem['client']>
      >((map, client) => {
        map[String(client.id)] = client;
        return map;
      }, {}),
    [clients]
  );
  const totals = lineItems.reduce<LineItemTotals>(
    (acc, item) => ({
      hours: acc.hours + toNumber(item.hours),
      subtotal: acc.subtotal + toNumber(item.subtotal),
      cleaningProducts: acc.cleaningProducts + toNumber(item.cleaning_products),
      total: acc.total + toNumber(item.total),
    }),
    {
      hours: 0,
      subtotal: 0,
      cleaningProducts: 0,
      total: 0,
    }
  );

  return (
    <Page title="Provider Invoice Preview">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading={`Provider Invoice Preview #${invoiceId}`}
          links={[
            { name: 'Dashboard', href: PATH_USER.root },
            { name: 'Invoice Dry Runs', href: invoicesPath('dryRuns') },
            {
              name: `Dry Run #${id}`,
              href: invoicesPath('dryRunDetails', { id }),
            },
            { name: `Invoice #${invoiceId}`, href: '' },
          ]}
        />

        {isLoading ? (
          <SkeletonLoadingTable />
        ) : isError || !invoice ? (
          <Alert severity="error">
            Unable to load provider invoice preview.
          </Alert>
        ) : (
          <Stack spacing={3}>
            <Card sx={{ p: 2.5 }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ md: 'center' }}
                justifyContent="space-between"
              >
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="h6">Invoice #{invoiceId}</Typography>
                    <Chip
                      size="small"
                      label="Dry run preview"
                      sx={{
                        color: 'info.dark',
                        bgcolor: alpha(theme.palette.info.main, 0.12),
                      }}
                    />
                  </Stack>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Provider {invoice.provider_id ?? '-'} ·{' '}
                    {formatDryRunDateRange(invoice.date_from, invoice.date_to)}
                  </Typography>
                </Stack>

                <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary' }}
                  >
                    Adjusted total
                  </Typography>
                  <Typography variant="h4">
                    {formatDryRunMoney(invoice.adjusted_total)}
                  </Typography>
                </Box>
              </Stack>

              <Divider sx={{ my: 2.5 }} />

              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6} md={3}>
                  <SummaryItem
                    label="Base total"
                    value={formatDryRunMoney(invoice.base_total)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <SummaryItem
                    label="Adjustments"
                    value={formatDryRunMoney(invoice.adjustments_total)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <SummaryItem
                    label="Line items"
                    value={invoice.item_count ?? lineItems.length}
                    helper={`${totals.hours} total hours`}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <SummaryItem
                    label="Calculated line total"
                    value={formatDryRunMoney(totals.total)}
                  />
                </Grid>
              </Grid>
            </Card>

            <Card sx={{ overflow: 'hidden' }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ sm: 'center' }}
                justifyContent="space-between"
                sx={{ px: 2.5, py: 2 }}
              >
                <Box>
                  <Typography variant="subtitle1">Line items</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {lineItems.length} jobs included in this preview
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={`Total ${formatDryRunMoney(totals.total)}`}
                  sx={{ fontWeight: 700 }}
                />
              </Stack>
              <Divider />
              <Scrollbar>
                <TableContainer sx={{ minWidth: 1120, position: 'relative' }}>
                  <Table size="small">
                    <TableHeadCustom
                      headLabel={INVOICE_DRY_RUN_LINE_ITEMS_TABLE_HEAD}
                    />
                    <TableBody>
                      {lineItems.map((item, index) => (
                        <TableRow
                          key={String(item.id ?? index)}
                          hover
                          sx={{
                            '&:last-of-type td': {
                              borderBottom: lineItems.length
                                ? `1px solid ${theme.palette.divider}`
                                : 0,
                            },
                          }}
                        >
                          <TableCell align="left">
                            {item.job_id ?? '-'}
                          </TableCell>
                          <TableCell align="left">
                            <ClientAccountLink
                              item={{
                                ...item,
                                client: resolveLineItemClient(
                                  item,
                                  clientsById
                                ),
                              }}
                            />
                          </TableCell>
                          <TableCell align="left" sx={{ maxWidth: 320 }}>
                            <Typography variant="body2" noWrap>
                              {item.description || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="left">
                            {formatDryRunDateTime(item.job_timestamp)}
                          </TableCell>
                          <TableCell align="right">
                            {item.hours ?? '-'}
                          </TableCell>
                          <TableCell align="right">
                            {formatDryRunMoney(item.price_per_hour)}
                          </TableCell>
                          <TableCell align="right">
                            {formatDryRunMoney(item.subtotal)}
                          </TableCell>
                          <TableCell align="right">
                            {formatDryRunMoney(item.cleaning_products)}
                          </TableCell>
                          <TableCell align="right">
                            {formatDryRunMoney(item.total)}
                          </TableCell>
                        </TableRow>
                      ))}

                      <TableNoData
                        isNotFound={!lineItems.length}
                        title="No line items found"
                      />

                      {!!lineItems.length && (
                        <TableRow
                          sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.04),
                            '& td': {
                              fontWeight: 700,
                              borderBottom: 0,
                            },
                          }}
                        >
                          <TableCell colSpan={4} align="right">
                            Line item totals
                          </TableCell>
                          <TableCell align="right">{totals.hours}</TableCell>
                          <TableCell align="right">-</TableCell>
                          <TableCell align="right">
                            {formatDryRunMoney(totals.subtotal)}
                          </TableCell>
                          <TableCell align="right">
                            {formatDryRunMoney(totals.cleaningProducts)}
                          </TableCell>
                          <TableCell align="right">
                            {formatDryRunMoney(totals.total)}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Scrollbar>
            </Card>
          </Stack>
        )}
      </Container>
    </Page>
  );
}
